import React, { createContext, useContext, useEffect, useReducer, useCallback, useRef } from 'react';
import { SAMPLES, getSample } from '@/data/samples';
import { extractFromFile, extractFromUrl, pollRun, generateHighlightTitles } from '@/lib/api';

const HISTORY_KEY = 'simnopsis-run-history';
const MAX_HISTORY = 5;

// State shape
const initialState = {
  consoleMode: 'idle',            // 'idle' | 'processing' | 'complete'
  showcaseVisible: false,
  showcaseSkeleton: false,
  activeTab: 'cards',             // 'stack' | 'cards' | 'timeline' | 'refine'
  activeHighlightId: null,
  cleanupOn: false,
  uploadMode: 'quick',            // 'quick' | 'best' | 'deep'
  uploadLength: 'auto',
  focus: '',
  refineMessages: [],             // [{ role: 'user'|'assistant', text, at }]
  currentRun: null,               // { source, duration, highlights, themesFound, scanned, version, sampleId }
  runHistory: [],
  processingLogs: [],             // array of active mode B lines shown
  activeThemes: [],               // orb ids lit up so far in graph
  progressPct: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET': return { ...state, ...action.payload };
    case 'RESET_SESSION':
      return {
        ...state,
        consoleMode: 'idle',
        showcaseVisible: false,
        showcaseSkeleton: false,
        activeHighlightId: null,
        currentRun: null,
        refineMessages: [],
        processingLogs: [],
        activeThemes: [],
        progressPct: 0,
      };
    case 'LOAD_HISTORY': return { ...state, runHistory: action.payload };
    default: return state;
  }
}

const StoreCtx = createContext(null);

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const timeoutsRef = useRef([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) dispatch({ type: 'LOAD_HISTORY', payload: JSON.parse(raw) });
    } catch (e) { /* ignore */ }
  }, []);

  const clearTimers = () => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  };

  const saveRunToHistory = useCallback((run) => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const list = raw ? JSON.parse(raw) : [];
      // Determine version
      const sameSource = list.filter(r => r.sampleId === run.sampleId || r.source === run.source);
      const version = sameSource.length + 1;
      // Retro-tag the first entry as v1 if this is the 2nd occurrence
      if (sameSource.length === 1 && !sameSource[0].version) {
        const idx = list.findIndex(r => r === sameSource[0]);
        if (idx >= 0) list[idx] = { ...list[idx], version: 1 };
      }
      const entry = { ...run, version, timestamp: new Date().toISOString(), status: 'done' };
      const next = [entry, ...list].slice(0, MAX_HISTORY);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      dispatch({ type: 'LOAD_HISTORY', payload: next });
    } catch (e) { /* ignore */ }
  }, []);

  const removeFromHistory = useCallback((timestamp) => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const next = list.filter(r => r.timestamp !== timestamp);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      dispatch({ type: 'LOAD_HISTORY', payload: next });
    } catch (e) {}
  }, []);

  const clearHistory = useCallback(() => {
    localStorage.removeItem(HISTORY_KEY);
    dispatch({ type: 'LOAD_HISTORY', payload: [] });
  }, []);

  // Trigger a processing sequence for a given sample id (or a "custom" pseudo run)
  const runExtraction = useCallback((opts = {}) => {
    clearTimers();
    // Pick sample: prefer explicit sampleId; else default to first sample as a mocked "custom" result
    const sampleId = opts.sampleId || 'podcast-founder-42';
    const sample = getSample(sampleId);
    if (!sample) return;

    // Reset console + hide showcase before running
    dispatch({ type: 'SET', payload: {
      consoleMode: 'processing',
      showcaseVisible: false,
      showcaseSkeleton: false,
      processingLogs: [],
      activeThemes: [],
      progressPct: 0,
      activeHighlightId: null,
      refineMessages: [],
    }});

    // Build log sequence
    const cleanupOn = state.cleanupOn;
    const lines = [];
    if (cleanupOn) {
      lines.push({ cat: 'SYS', text: `Cleaning audio — removing silence and dead air...`, t: '00:00' });
      lines.push({ cat: 'SYS', text: `3:47 of silence trimmed · audio normalized`, t: '00:02' });
    }
    const base = cleanupOn ? 4 : 0;
    lines.push({ cat: 'SYS', text: `Ingesting audio — ${sample.source} (${sample.duration})`, t: fmt(base + 0) });
    lines.push({ cat: 'SYS', text: `Scanning waveform for speech patterns...`, t: fmt(base + 2) });
    lines.push({ cat: 'SYS', text: `Segmenting by silence gaps and energy levels`, t: fmt(base + 4) });
    lines.push({ cat: 'THEME', theme: sample.highlights[0].theme, tag: sample.highlights[0].themeName, text: `Theme detected — confidence 0.94`, t: fmt(base + 6) });
    lines.push({ cat: 'HIGHLIGHT', text: `Scoring moment at ${sample.highlights[0].start} — emotional weight: high`, t: fmt(base + 8) });
    lines.push({ cat: 'THEME', theme: sample.highlights[1].theme, tag: sample.highlights[1].themeName, text: `Theme detected — confidence 0.91`, t: fmt(base + 10) });
    lines.push({ cat: 'HIGHLIGHT', text: `"${sample.highlights[1].title}" — rating ${sample.highlights[1].rating}`, t: fmt(base + 12) });
    lines.push({ cat: 'THEME', theme: sample.highlights[2].theme, tag: sample.highlights[2].themeName, text: `Theme detected — confidence 0.88`, t: fmt(base + 14) });
    lines.push({ cat: 'SYS', text: `Cross-referencing theme clusters...`, t: fmt(base + 16) });
    lines.push({ cat: 'HIGHLIGHT', text: `${sample.highlights.length * 4} highlights scored · ${sample.themesFound} themes mapped · Top pick: ${sample.highlights[0].rating}`, t: fmt(base + 18) });
    lines.push({ cat: 'DONE', text: `Extraction complete — results ready`, t: fmt(base + 20) });

    const stepMs = 700;
    const themesOrder = sample.highlights.map(h => h.theme);

    lines.forEach((line, idx) => {
      const to = setTimeout(() => {
        // Append log line
        dispatch({ type: 'SET', payload: {
          processingLogs: buildLogArray(lines, idx),
          progressPct: Math.round(((idx + 1) / lines.length) * 100),
        }});

        // Theme graph orbs light up on theme detection lines
        if (line.cat === 'THEME') {
          const themeIdx = themesOrder.indexOf(line.theme);
          const cur = collectActiveThemes(themesOrder, themeIdx + 1);
          dispatch({ type: 'SET', payload: { activeThemes: cur }});
        }
        // On cross-referencing line — light remaining primary orbs and sub-themes
        if (line.text.startsWith('Cross-referencing')) {
          dispatch({ type: 'SET', payload: { activeThemes: ['cyan','mint','violet','amber','coral','sub1','sub2','sub3'] }});
        }
      }, idx * stepMs);
      timeoutsRef.current.push(to);
    });

    // On completion
    const totalMs = lines.length * stepMs + 200;
    const doneTo = setTimeout(() => {
      const run = {
        sampleId,
        source: sample.source,
        duration: sample.duration,
        scanned: sample.scanned,
        themesFound: sample.themesFound,
        category: sample.category,
        categoryColor: sample.categoryColor,
        highlights: sample.highlights,
        mode: state.uploadMode,
        length: state.uploadLength,
        focus: state.focus,
        cleanupOn: cleanupOn,
      };
      dispatch({ type: 'SET', payload: {
        consoleMode: 'complete',
        currentRun: run,
        showcaseSkeleton: true,
      }});

      // Skeleton hold
      const skelTo = setTimeout(() => {
        dispatch({ type: 'SET', payload: { showcaseVisible: true, showcaseSkeleton: false }});
        // Smooth-scroll to showcase
        setTimeout(() => {
          const el = document.getElementById('showcase');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }, 380);
      timeoutsRef.current.push(skelTo);

      saveRunToHistory(run);
    }, totalMs);
    timeoutsRef.current.push(doneTo);
  }, [state.cleanupOn, state.uploadMode, state.uploadLength, state.focus, saveRunToHistory]);

  // Load a saved run instantly (no processing animation)
  const loadRun = useCallback((run) => {
    clearTimers();
    dispatch({ type: 'SET', payload: {
      consoleMode: 'complete',
      currentRun: run,
      showcaseVisible: true,
      showcaseSkeleton: false,
      activeHighlightId: null,
      activeThemes: ['cyan','mint','violet','amber','coral','sub1','sub2','sub3'],
      processingLogs: [{ cat: 'DONE', text: 'Loaded from history · results ready', t: '00:00' }],
      progressPct: 100,
    }});
    setTimeout(() => {
      const el = document.getElementById('showcase');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, []);

  const clearSession = useCallback(() => {
    clearTimers();
    localStorage.removeItem(HISTORY_KEY);
    dispatch({ type: 'RESET_SESSION' });
    dispatch({ type: 'LOAD_HISTORY', payload: [] });
  }, []);

  // =============================================
  // REAL EXTRACTION — via Simnopsis-HX backend
  // =============================================
  const runRealExtraction = useCallback(async ({ file, url }) => {
    clearTimers();

    dispatch({ type: 'SET', payload: {
      consoleMode: 'processing',
      showcaseVisible: false,
      showcaseSkeleton: false,
      processingLogs: [],
      activeThemes: [],
      progressPct: 0,
      activeHighlightId: null,
      currentRun: null,
      refineMessages: [],
    }});

    // addLog is unused — logs are set directly in poll callbacks below

    try {
      // Step 1: Start extraction
      const sourceName = file?.name || url || 'Unknown source';
      const sourceShort = sourceName.length > 52 ? `${sourceName.slice(0, 49)}…` : sourceName;

      dispatch({ type: 'SET', payload: {
        processingLogs: [{ cat: 'SYS', text: 'Uploading audio to Simnopsis-HX...', t: '00:00' }],
        progressPct: 5,
      }});

      let result;
      if (file) {
        result = await extractFromFile(file, {
          mode: state.uploadMode,
          focus: state.focus,
          tiers: 'L,S,P',
        });
      } else if (url) {
        result = await extractFromUrl(url, {
          mode: state.uploadMode,
          focus: state.focus,
          tiers: 'L,S,P',
        });
      } else {
        throw new Error('No file or URL provided');
      }

      const runId = result.run_id;
      const runRef = formatRunRef(runId);

      const baseLogs = [
        { cat: 'SYS', text: 'Uploading audio to Simnopsis-HX...', t: '00:00' },
        { cat: 'SYS', text: `Source · ${sourceShort}`, t: '00:01' },
        { cat: 'SYS', text: `Run started — ${runRef}`, t: '00:02' },
      ];

      dispatch({ type: 'SET', payload: {
        processingLogs: baseLogs,
        progressPct: 10,
        activeThemes: [],
      }});

      // Transcription can take ~60–120s — drive % + Simscribe log refs from elapsed time
      let transcribeStartedAt = null;
      const TRANSCRIBE_WINDOW_MS = 90000; // ~90s climb toward ~55%

      const simscribeBeat = (elapsedSec) => {
        if (elapsedSec < 8) return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · job accepted · Whisper online', t: fmt(elapsedSec) };
        if (elapsedSec < 22) return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · ingesting audio stream…', t: fmt(elapsedSec) };
        if (elapsedSec < 40) return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · Tier Lite decoding…', t: fmt(elapsedSec) };
        if (elapsedSec < 60) return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · Tier Standard refining…', t: fmt(elapsedSec) };
        if (elapsedSec < 85) return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · Tier Pro + merge running…', t: fmt(elapsedSec) };
        return { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · finalizing transcript…', t: fmt(elapsedSec) };
      };

      // Step 2: Poll for completion
      const finalRun = await pollRun(runId, (run) => {
        if (run.status === 'transcribing') {
          if (!transcribeStartedAt) transcribeStartedAt = Date.now();
          const elapsedMs = Date.now() - transcribeStartedAt;
          const elapsedSec = Math.floor(elapsedMs / 1000);
          // Slow climb: 12% → 55% over ~90s (never hits 100 while still transcribing)
          const climb = Math.min(1, elapsedMs / TRANSCRIBE_WINDOW_MS);
          const pct = Math.round(12 + climb * 43);

          const statusLogs = [
            ...baseLogs,
            { cat: 'STEP', text: 'Transcribing audio via Simscribe-X...', t: '00:04' },
            simscribeBeat(Math.max(5, elapsedSec)),
          ];

          dispatch({ type: 'SET', payload: {
            processingLogs: statusLogs,
            progressPct: pct,
            activeThemes: ['coral'],
          }});
        } else if (run.status === 'detecting') {
          dispatch({ type: 'SET', payload: {
            processingLogs: [
              ...baseLogs,
              { cat: 'STEP', text: 'Transcription complete · Simscribe-X', t: '00:04' },
              { cat: 'STEP', tag: 'SCRIBE', text: 'Simscribe-X · transcript delivered', t: '00:05' },
              { cat: 'THEME', theme: 'cyan', tag: 'DETECT', text: 'Detecting highlights...', t: '00:06' },
            ],
            progressPct: 62,
            activeThemes: ['cyan', 'violet', 'coral'],
          }});
        } else if (run.status === 'slicing') {
          dispatch({ type: 'SET', payload: {
            processingLogs: [
              ...baseLogs,
              { cat: 'STEP', text: 'Transcription complete · Simscribe-X', t: '00:04' },
              { cat: 'THEME', theme: 'cyan', tag: 'DETECT', text: 'Highlights detected', t: '00:06' },
              { cat: 'HIGHLIGHT', text: 'Slicing audio clips...', t: '00:08' },
            ],
            progressPct: 82,
            activeThemes: ['cyan', 'mint', 'violet', 'amber', 'coral'],
          }});
        }
      }, 2000);

      // Step 3: Complete — transform to UI format
      const clips = finalRun.clips || [];
      const clipsByHighlight = Object.fromEntries(
        clips
          .filter((c) => c.highlight_id || c.highlightId)
          .map((c) => [c.highlight_id || c.highlightId, c]),
      );

      let highlights = (finalRun.highlights || []).map((h, i) => {
        const startSec = Number(h.startSec ?? h.start ?? 0);
        const durationSec = Number(h.durationSec ?? ((h.end != null && h.start != null) ? (h.end - h.start) : 0));
        const quote = (h.quote || h.excerpt || h.text || '').trim();
        const rawTitle = (h.title || '').trim();
        const titleLooksLikeQuote = !rawTitle
          || rawTitle.length > 42
          || (quote && quote.toLowerCase().startsWith(rawTitle.replace(/\.\.\.$/, '').toLowerCase().slice(0, 24)));
        const title = titleLooksLikeQuote ? '' : rawTitle;
        const rating = Number(h.rating ?? (h.score != null ? h.score * 10 : 7.5));
        const highlightId = h.id || `hl_${i}`;
        const clip = clipsByHighlight[highlightId] || clips[i] || null;
        const clipId = h.clipId || h.clip_id || clip?.clip_id || clip?.clipId || null;

        return {
          id: highlightId,
          title,
          quote: quote || title || `Highlight ${i + 1}`,
          excerpt: (h.excerpt || quote || title || '').trim(),
          theme: h.theme || ['cyan', 'mint', 'violet', 'amber', 'coral'][i % 5],
          themeName: (h.themeName || 'HIGHLIGHT').toUpperCase(),
          start: fmt(startSec),
          startSec: Math.round(startSec * 10) / 10,
          durationSec: Math.round(durationSec * 10) / 10,
          length: h.length || (clip?.duration_ms != null ? fmt(clip.duration_ms / 1000) : fmt(durationSec)),
          rating: Math.round(Math.min(9.8, Math.max(0, rating)) * 10) / 10,
          score: h.score,
          clipId,
        };
      });

      // Step 3b: Dual editorial keys — summary slot + title slot (alternating)
      dispatch({ type: 'SET', payload: {
        processingLogs: [
          { cat: 'SYS', text: `Audio uploaded · ${runRef}`, t: '00:00' },
          { cat: 'SYS', text: `Source · ${sourceShort}`, t: '00:01' },
          { cat: 'SYS', text: `Run started — ${runRef}`, t: '00:02' },
          { cat: 'STEP', text: 'Transcription complete', t: '00:04' },
          { cat: 'THEME', theme: 'cyan', tag: 'DETECT', text: `${highlights.length} highlights detected`, t: '00:06' },
          { cat: 'HIGHLIGHT', text: `${(finalRun.clips || []).length} clips sliced`, t: '00:08' },
          { cat: 'STEP', tag: 'EDIT', text: 'dual keys · summary + title slots', t: '00:09' },
          { cat: 'STEP', tag: 'EDIT', text: 'summary key drafting snippet insights…', t: '00:09' },
        ],
        progressPct: 90,
      }});

      let titleProcess = null;
      try {
        dispatch({ type: 'SET', payload: {
          processingLogs: [
            { cat: 'SYS', text: `Audio uploaded · ${runRef}`, t: '00:00' },
            { cat: 'SYS', text: `Source · ${sourceShort}`, t: '00:01' },
            { cat: 'SYS', text: `Run started — ${runRef}`, t: '00:02' },
            { cat: 'STEP', text: 'Transcription complete', t: '00:04' },
            { cat: 'THEME', theme: 'cyan', tag: 'DETECT', text: `${highlights.length} highlights detected`, t: '00:06' },
            { cat: 'HIGHLIGHT', text: `${(finalRun.clips || []).length} clips sliced`, t: '00:08' },
            { cat: 'STEP', tag: 'EDIT', text: 'dual keys · summary + title slots', t: '00:09' },
            { cat: 'STEP', tag: 'EDIT', text: 'summary key drafting snippet insights…', t: '00:09' },
            { cat: 'STEP', tag: 'EDIT', text: 'title key writing card titles…', t: '00:10' },
          ],
          progressPct: 94,
        }});

        const titleResp = await generateHighlightTitles(
          highlights.map((h) => ({ id: h.id, text: h.quote || h.excerpt || '' })),
        );
        titleProcess = titleResp.process || null;
        const byId = Object.fromEntries((titleResp.titles || []).map((t) => [t.id, t]));
        highlights = highlights.map((h, i) => {
          const gen = byId[h.id];
          return {
            ...h,
            title: gen?.title || localShortTitle(h.quote || h.excerpt, i),
            summary: gen?.summary || '',
          };
        });
      } catch {
        highlights = highlights.map((h, i) => ({
          ...h,
          title: localShortTitle(h.quote || h.excerpt, i),
          summary: '',
        }));
      }

      const dualReady = Boolean(titleProcess?.used_gemini || titleProcess?.used_dual_keys);
      const editorialDoneLine = dualReady
        ? {
            cat: 'STEP',
            tag: 'EDIT',
            text: `dual-key titles ready · ${highlights.length} cards · slots summary↔title`,
            t: '00:11',
          }
        : {
            cat: 'STEP',
            tag: 'EDIT',
            text: `titles ready · heuristic fallback · ${highlights.length} cards`,
            t: '00:11',
          };

      const highlightEnd = highlights.reduce(
        (max, h) => Math.max(max, (Number(h.startSec) || 0) + (Number(h.durationSec) || 0)),
        0,
      );
      const transcriptSec = Number(finalRun.transcript?.elapsed_seconds) || 0;
      const durationSec = Math.max(transcriptSec, highlightEnd, 1);
      const clipTotalSec = highlights.reduce((sum, h) => sum + (Number(h.durationSec) || 0), 0);

      const run = {
        runId,
        runRef,
        source: file ? file.name : url,
        duration: fmt(durationSec),
        durationSec: Math.round(durationSec * 10) / 10,
        totalClipTime: `${Math.round(clipTotalSec)}s`,
        scanned: highlights.length * 30,
        themesFound: new Set(highlights.map(h => h.theme)).size,
        highlights,
        clips: finalRun.clips || [],
        mode: state.uploadMode,
        focus: state.focus,
      };

      dispatch({ type: 'SET', payload: {
        consoleMode: 'complete',
        currentRun: run,
        showcaseSkeleton: true,
        processingLogs: [
          { cat: 'SYS', text: `Audio uploaded · ${runRef}`, t: '00:00' },
          { cat: 'SYS', text: `Source · ${sourceShort}`, t: '00:01' },
          { cat: 'SYS', text: `Run started — ${runRef}`, t: '00:02' },
          { cat: 'STEP', text: 'Transcription complete', t: '00:04' },
          { cat: 'THEME', theme: 'cyan', tag: 'DETECT', text: `${highlights.length} highlights detected`, t: '00:06' },
          { cat: 'HIGHLIGHT', text: `${(finalRun.clips || []).length} clips sliced`, t: '00:08' },
          { cat: 'STEP', tag: 'EDIT', text: 'dual keys · summary + title slots', t: '00:09' },
          { cat: 'STEP', tag: 'EDIT', text: 'summary key drafting snippet insights…', t: '00:09' },
          { cat: 'STEP', tag: 'EDIT', text: 'title key writing card titles…', t: '00:10' },
          editorialDoneLine,
          { cat: 'DONE', text: `Extraction complete — ${runRef} ready`, t: '00:12' },
        ],
        progressPct: 100,
        activeThemes: ['cyan', 'mint', 'violet', 'amber', 'coral', 'sub1', 'sub2', 'sub3'],
      }});

      // Show showcase after skeleton
      setTimeout(() => {
        dispatch({ type: 'SET', payload: { showcaseVisible: true, showcaseSkeleton: false } });
        setTimeout(() => {
          const el = document.getElementById('showcase');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }, 380);

      saveRunToHistory(run);

    } catch (err) {
      dispatch({ type: 'SET', payload: {
        consoleMode: 'complete',
        processingLogs: [
          { cat: 'SYS', text: 'Extraction failed', t: '00:00' },
          { cat: 'DONE', text: err.message || 'Unknown error', t: '00:01' },
        ],
        progressPct: 0,
      }});
    }
  }, [state.uploadMode, state.focus, saveRunToHistory]);

  /** Rebuild current highlights to better match a free-text preference. */
  const refineCuts = useCallback(async (prompt) => {
    const text = String(prompt || '').trim();
    const run = state.currentRun;
    if (!text || !run?.highlights?.length) return false;

    const stop = new Set([
      'more', 'less', 'only', 'just', 'with', 'from', 'that', 'this', 'these',
      'those', 'have', 'want', 'keep', 'make', 'please', 'about', 'into', 'than',
      'them', 'they', 'your', 'ours', 'clips', 'audio', 'cuts', 'focus', 'should',
    ]);
    const keywords = text
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .map((w) => w.trim())
      .filter((w) => w.length > 3 && !stop.has(w));

    const preferShort = /\b(short|punchy|tight|brief|quick)\b/i.test(text);
    const preferLong = /\b(long|deeper|context|full)\b/i.test(text);

    const ranked = run.highlights
      .map((h, index) => {
        const hay = `${h.title || ''} ${h.quote || ''} ${h.excerpt || ''} ${h.themeName || ''}`.toLowerCase();
        let score = Number(h.rating) || 0;
        keywords.forEach((k) => {
          if (hay.includes(k)) score += 3;
        });
        const dur = Number(h.durationSec) || 0;
        if (preferShort) score += dur > 0 && dur <= 20 ? 2 : -1;
        if (preferLong) score += dur >= 12 ? 2 : -1;
        return { h, score, index };
      })
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .map(({ h }, i) => ({
        ...h,
        rating: Math.min(9.9, Math.round(((Number(h.rating) || 8) + Math.max(0, 0.4 - i * 0.08)) * 10) / 10),
      }));

    const nextRun = {
      ...run,
      highlights: ranked,
      focus: text,
      version: (run.version || 1) + 1,
      refinedAt: Date.now(),
    };

    const prior = state.refineMessages || [];
    const refineMessages = [
      ...prior,
      { role: 'user', text, at: Date.now() },
      {
        role: 'assistant',
        text: `Rebuilt v${nextRun.version} around “${text}”. Open Cards or Stack to review the new order.`,
        at: Date.now() + 1,
      },
    ];

    // Brief processing feel without leaving showcase
    await new Promise((r) => setTimeout(r, 450));

    dispatch({ type: 'SET', payload: {
      focus: text,
      currentRun: nextRun,
      refineMessages,
      activeHighlightId: nextRun.highlights[0]?.id || null,
    }});
    saveRunToHistory(nextRun);
    return true;
  }, [state.currentRun, state.refineMessages, saveRunToHistory]);

  const value = {
    state,
    setState: (payload) => dispatch({ type: 'SET', payload }),
    runExtraction,
    runRealExtraction,
    refineCuts,
    loadRun,
    clearSession,
    removeFromHistory,
    clearHistory,
  };

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}

function fmt(secs) {
  const total = Math.max(0, Math.round(Number(secs) || 0));
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/** Human-readable run ref from UUID — e.g. SN-EB85-C086 */
function formatRunRef(runId) {
  const hex = String(runId || '').replace(/-/g, '').toUpperCase();
  if (hex.length < 8) return 'SN-PENDING';
  return `SN-${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
}

/** Offline fallback when /v1/titles is unavailable — contiguous readable phrase */
function localShortTitle(text, index = 0) {
  const hanging = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'of', 'to', 'for', 'from', 'in', 'on',
    'at', 'by', 'with', 'as', 'is', 'are', 'was', 'were', 'we', 'you', 'they',
    'i', 'it', 'this', 'that', 'have', 'has', 'had', 'about',
  ]);
  const lead = new Set([
    ...hanging, 'so', 'now', 'just', 'well', 'today', 'then', 'spoken', 'speak',
    'talking', 'said', 'say',
  ]);
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return `Highlight ${index + 1}`;

  const ofMatch = raw.match(/\b([A-Za-z][A-Za-z']*)\s+of\s+(?:the\s+)?([A-Za-z][A-Za-z']*)\b/i);
  if (ofMatch && !['aspect', 'kind', 'sort', 'thing', 'matter', 'level', 'part'].includes(ofMatch[1].toLowerCase())) {
    return titleCaseWords([ofMatch[1], 'of', ofMatch[2]]);
  }
  const lightly = raw.match(/\b((?:do not|don't|never|not ever)\s+take\s+(?:this|it|that|\w+)\s+lightly)\b/i);
  if (lightly) return titleCaseWords(lightly[1].match(/[A-Za-z0-9']+/g) || []);
  const hundred = raw.match(/\b((?:a|an)\s+\w+\s+\w+)\b/i);
  if (hundred) return titleCaseWords(hundred[1].match(/[A-Za-z0-9']+/g) || []);

  let words = raw.match(/[A-Za-z0-9']+/g) || [];
  while (words.length && lead.has(words[0].toLowerCase())) words = words.slice(1);
  words = words.slice(0, 6);
  while (words.length && hanging.has(words[words.length - 1].toLowerCase())) words.pop();
  if (words.length < 2) return `Highlight ${index + 1}`;
  return titleCaseWords(words);
}

function titleCaseWords(words) {
  const small = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'of', 'to', 'for', 'from', 'in', 'on', 'at', 'by', 'with']);
  return words
    .map((w, i) => {
      const low = w.toLowerCase();
      if (i > 0 && small.has(low)) return low;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

function buildLogArray(lines, upToIdx) {
  return lines.slice(0, upToIdx + 1);
}

function collectActiveThemes(order, count) {
  return order.slice(0, count);
}
